import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { TriangleAlert } from "lucide-react";
import Seo from "../components/Seo";
import { Eyebrow, MaskedLines } from "../components/Reveal";
import { SPECIES, PRODUCT_BY_SLUG } from "../data/products";

const Applications = () => {
  const [active, setActive] = useState(SPECIES[0].slug);
  const species = SPECIES.find((s) => s.slug === active);

  return (
    <div data-testid="page-applications">
      <Seo
        title="Applications by Species"
        description="Aurik feed additive programmes for calf & ruminant, poultry, aqua and swine — palatability, toxin management, gut health and mineral nutrition per species."
      />

      <section className="grain mesh-glow relative overflow-hidden pb-16 pt-40 lg:pt-48">
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Eyebrow dark>Applications / species</Eyebrow>
          <MaskedLines
            as="h1"
            delay={0.2}
            className="mt-7 max-w-2xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
            lines={["Matched to the animal,", "not just the ration."]}
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-28 pt-12 sm:px-8">
        <div className="flex flex-wrap gap-2.5" data-testid="species-tabs" role="tablist" aria-label="Species">
          {SPECIES.map((s, i) => (
            <button
              key={s.slug}
              role="tab"
              aria-selected={active === s.slug}
              data-testid={`species-tab-${s.slug}`}
              onClick={() => setActive(s.slug)}
              className={`flex items-center gap-3 rounded-full border px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                active === s.slug
                  ? "border-emerald-800 bg-emerald-900 text-white"
                  : "border-slate-300 bg-white text-slate-600 hover:border-teal-600 hover:text-teal-700"
              }`}
            >
              <span className="text-[9px] opacity-60">0{i + 1}</span>
              {s.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={species.slug}
            data-testid={`species-panel-${species.slug}`}
            role="tabpanel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 grid items-start gap-12 lg:grid-cols-[0.9fr_1.1fr]"
          >
            <div className="clip-frame overflow-hidden">
              <img src={species.image} alt={`${species.label} feed programme`} className="h-72 w-full object-cover lg:h-[460px]" />
            </div>

            <div>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-night">{species.label}</h2>
              <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-base">{species.intro}</p>

              <h3 className="mt-10 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-teal-700">
                Nutritional challenges we target
              </h3>
              <ul className="mt-5 space-y-3.5">
                {species.challenges.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-sm text-slate-700">
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    {c}
                  </li>
                ))}
              </ul>

              <h3 className="mt-10 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-teal-700">
                Recommended Aurik stack
              </h3>
              <div className="mt-5 flex flex-wrap gap-3">
                {species.recommended.map((slug) => {
                  const p = PRODUCT_BY_SLUG[slug];
                  return (
                    <Link
                      key={slug}
                      to={`/products/${slug}`}
                      data-testid={`species-product-link-${slug}`}
                      className="rounded-full border border-emerald-300 bg-emerald-50 px-4 py-2 font-mono text-[11px] font-semibold text-emerald-900 transition-colors hover:bg-emerald-900 hover:text-white"
                    >
                      {p.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </section>
    </div>
  );
};

export default Applications;
